import type { ISoraVideoTool } from '~/interfaces/IMcpServer.js';
import { createVideoTask, pollTaskUntilComplete, SoraApiError } from '~/utils/soraApi.js';
import { downloadVideoFromUrl, readImageAsBase64, VideoSaverError } from '~/utils/videoSaver.js';
import fs from 'fs/promises';

/**
 * 图生视频工具
 * 根据图片和文本描述生成视频
 */
export const createVideoFromImageTool: ISoraVideoTool = {
  name: 'createVideoFromImage',
  description: 'Generate videos from images with text instructions using Sora AI. You can provide either a local image path or an image URL.',
  inputSchema: {
    type: 'object',
    properties: {
      prompt: {
        type: 'string',
        description: 'Text description of how to animate the image or what video to create based on the image.',
      },
      inputImage: {
        type: 'string',
        description: 'Input image: either a local file path (e.g., /path/to/image.jpg) or a URL (e.g., https://example.com/image.png).',
      },
      model: {
        type: 'string',
        enum: ['sora-2', 'sora-2-pro'],
        default: 'sora-2-pro',
        description: 'Model to use. Recommended: sora-2-pro for image-to-video tasks.',
      },
      orientation: {
        type: 'string',
        enum: ['portrait', 'landscape'],
        default: 'landscape',
        description: 'Video orientation: portrait (9:16) or landscape (16:9).',
      },
      size: {
        type: 'string',
        enum: ['small', 'large'],
        default: 'small',
        description: 'Video resolution size.',
      },
      duration: {
        type: 'number',
        enum: [10, 15, 25],
        default: 15,
        description: 'Video duration in seconds. Note: sora-2 only supports 10/15s, sora-2-pro supports 15/25s.',
      },
    },
    required: ['prompt', 'inputImage'],
  },

  async run(args: any) {
    console.error('========================================');
    console.error('[CreateVideoFromImage] Tool execution started');
    console.error('========================================');

    try {
      // 提取并验证参数
      const {
        prompt,
        inputImage,
        model = 'sora-2-pro',
        orientation = 'landscape',
        size = 'small',
        duration = 15,
      } = args;

      // 参数验证
      if (!prompt || typeof prompt !== 'string') {
        return {
          content: [{
            type: 'text',
            text: 'Error: prompt is required and must be a string',
          }],
          isError: true,
        };
      }

      if (!inputImage || typeof inputImage !== 'string') {
        return {
          content: [{
            type: 'text',
            text: 'Error: inputImage is required and must be a string (file path or URL)',
          }],
          isError: true,
        };
      }

      // 验证模型和时长的组合
      if (model === 'sora-2' && duration === 25) {
        return {
          content: [{
            type: 'text',
            text: 'Error: sora-2 model only supports duration of 10 or 15 seconds. Please use sora-2-pro for 25 second videos.',
          }],
          isError: true,
        };
      }

      console.error(`[CreateVideoFromImage] Parameters:`);
      console.error(`  - Prompt: ${prompt.substring(0, 100)}${prompt.length > 100 ? '...' : ''}`);
      console.error(`  - Input Image: ${inputImage}`);
      console.error(`  - Model: ${model}`);
      console.error(`  - Orientation: ${orientation}`);
      console.error(`  - Size: ${size}`);
      console.error(`  - Duration: ${duration}s`);

      // 步骤 1: 处理输入图片
      console.error('\n[CreateVideoFromImage] Step 1/4: Processing input image...');
      let imageUrl: string;

      // 判断输入是 URL 还是本地路径
      if (inputImage.startsWith('http://') || inputImage.startsWith('https://')) {
        // 输入是 URL，直接使用
        console.error('[CreateVideoFromImage] Input is a URL, using directly');
        imageUrl = inputImage;
      } else {
        // 输入是本地路径，需要读取文件
        console.error('[CreateVideoFromImage] Input is a local file path');

        // 验证文件是否存在
        try {
          await fs.access(inputImage);
        } catch (error) {
          return {
            content: [{
              type: 'text',
              text: `Error: Image file not found at path: ${inputImage}`,
            }],
            isError: true,
          };
        }

        // 读取图片并转换为 Base64 Data URL
        // 注意：这里假设 Sora API 接受 Base64 格式
        // 如果 API 只接受 URL，您可能需要先上传图片到临时存储
        imageUrl = await readImageAsBase64(inputImage);
        console.error('[CreateVideoFromImage] Image read and encoded as Base64');
      }

      // 步骤 2: 创建视频生成任务
      console.error('\n[CreateVideoFromImage] Step 2/4: Creating video generation task...');
      const taskId = await createVideoTask({
        prompt,
        model: model as 'sora-2' | 'sora-2-pro',
        orientation: orientation as 'portrait' | 'landscape',
        size: size as 'small' | 'large',
        duration: duration as 10 | 15 | 25,
        images: [imageUrl], // 图生视频模式
      });

      // 步骤 3: 轮询直到任务完成
      console.error(`\n[CreateVideoFromImage] Step 3/4: Polling task ${taskId} until completion...`);
      const videoUrl = await pollTaskUntilComplete(taskId);

      // 步骤 4: 下载视频
      console.error('\n[CreateVideoFromImage] Step 4/4: Downloading video...');
      const videoPath = await downloadVideoFromUrl(videoUrl);

      console.error('\n========================================');
      console.error('[CreateVideoFromImage] SUCCESS!');
      console.error(`[CreateVideoFromImage] Video saved at: ${videoPath}`);
      console.error('========================================\n');

      // 返回符合 MCP 规范的结果
      return {
        content: [{
          type: 'text',
          text: `Video generated from image successfully!\n\nPrompt: ${prompt}\nInput Image: ${inputImage}\nModel: ${model}\nDuration: ${duration}s\nOrientation: ${orientation}\n\nVideo saved at: ${videoPath}`,
        }],
      };

    } catch (error) {
      console.error('\n========================================');
      console.error('[CreateVideoFromImage] ERROR occurred');
      console.error('========================================');

      if (error instanceof SoraApiError) {
        console.error(`[CreateVideoFromImage] Sora API Error: ${error.message}`);
        return {
          content: [{
            type: 'text',
            text: `Sora API Error: ${error.message}`,
          }],
          isError: true,
        };
      }

      if (error instanceof VideoSaverError) {
        console.error(`[CreateVideoFromImage] Video Save Error: ${error.message}`);
        return {
          content: [{
            type: 'text',
            text: `Failed to process image or download video: ${error.message}`,
          }],
          isError: true,
        };
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[CreateVideoFromImage] Unexpected error: ${errorMessage}`);

      return {
        content: [{
          type: 'text',
          text: `An unexpected error occurred: ${errorMessage}`,
        }],
        isError: true,
      };
    }
  },
};
